import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

import { FireForesightService } from 'src/api/services';
import { Site } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';

@Component({
  selector: 'app-fire-foresight-sites',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ModalModule
  ],
  templateUrl: './fire-foresight-sites.component.html',
  styleUrls: ['./fire-foresight-sites.component.css']
})
export class FireForesightSitesComponent implements OnInit {

  // When true, this component is embedded inside another page (e.g. a tab on the
  // Fire Foresight Admin page) and should not render its own breadcrumb.
  @Input() embedded = false;

  public sites: Site[] = [];
  public loading = false;
  public page = 1;
  public pageSize = 20;
  public totalCount = 0;

  // Mirrors the fixed set of supported camera FOVs/photo counts used elsewhere in the app.
  public fieldOfViewOptions: { value: number; label: string }[] = [
    { value: 30, label: '30° - 12 photos' },
    { value: 36, label: '36° - 10 photos' },
    { value: 45, label: '45° - 8 photos' },
    { value: 60, label: '60° - 6 photos' }
  ];

  // Mirrors the fixed set of supported camera elevation angles used elsewhere in the app.
  public cameraElevationOptions: { value: number; label: string }[] = [
    { value: 5, label: '5°' },
    { value: 4, label: '4°' },
    { value: 3, label: '3°' },
    { value: 2, label: '2°' },
    { value: 1, label: '1°' },
    { value: 0, label: '0°' },
    { value: -1, label: '-1°' },
    { value: -2, label: '-2°' },
    { value: -3, label: '-3°' },
    { value: -4, label: '-4°' },
    { value: -5, label: '-5°' },
    { value: -6, label: '-6°' },
    { value: -7, label: '-7°' },
    { value: -8, label: '-8°' },
    { value: -9, label: '-9°' }
  ];

  // Add form state
  public newSite: Partial<Site> = {};
  public newTouched: { [key: string]: boolean } = {};

  // Edit form state
  public editSite: Site = {} as Site;
  public editTouched: { [key: string]: boolean } = {};

  constructor(
    public app: ApplicationConfigService,
    private fireForesightService: FireForesightService,
    private toasterService: ToastrService
  ) { }

  ngOnInit(): void {
    this.loadSites(1);
  }

  public loadSites(page: number = this.page): void {
    this.loading = true;
    // The backend's `page` param is 0-indexed, but the UI displays/tracks a 1-indexed
    // page number, so translate on the way in and back out.
    this.fireForesightService.FireForesightGetSites({
      page: page - 1,
      pageSize: this.pageSize
    }).subscribe({
      next: result => {
        this.sites = result.items || [];
        this.page = result.page + 1;
        this.pageSize = result.pageSize;
        this.totalCount = result.totalCount;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toasterService.error('Failed to load sites');
      }
    });
  }

  public get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  public previousPage(): void {
    if (this.page > 1) {
      this.loadSites(this.page - 1);
    }
  }

  public nextPage(): void {
    if (this.page < this.totalPages) {
      this.loadSites(this.page + 1);
    }
  }

  public getCustomerName(customerId: number | null | undefined): string {
    if (customerId == null) {
      return '';
    }
    const customer = this.app.customers?.find(c => c.customerId === customerId);
    return customer ? customer.customerName : `Customer #${customerId}`;
  }

  // ---- Add ----

  public openAddModal(): void {
    this.newSite = {};
    this.newTouched = {};
  }

  public markNewFieldTouched(field: string): void {
    this.newTouched[field] = true;
  }

  // Camera altitude/height and image dimensions are physical measurements and can't be negative.
  private isNonNegative(value: number | null | undefined): boolean {
    return value != null && value >= 0;
  }

  public isNewSiteValid(): boolean {
    return !!(this.newSite.name && this.newSite.siteUri)
      && this.isNonNegative(this.newSite.cameraAltitude)
      && this.newSite.cameraElevation != null
      && this.isNonNegative(this.newSite.cameraHeight)
      && this.newSite.fieldOfView != null
      && this.isNonNegative(this.newSite.imageWidth)
      && this.isNonNegative(this.newSite.imageHeight);
  }

  public saveNewSite(): void {
    ['name', 'siteUri', 'cameraAltitude', 'cameraElevation', 'cameraHeight', 'fieldOfView', 'imageWidth', 'imageHeight']
      .forEach(field => this.newTouched[field] = true);

    if (!this.isNewSiteValid()) {
      this.toasterService.error('Please fill in all required fields');
      return;
    }

    this.fireForesightService.FireForesightCreateSite({
      Name: this.newSite.name,
      SiteUri: this.newSite.siteUri,
      CameraAltitude: this.newSite.cameraAltitude,
      CameraElevation: this.newSite.cameraElevation,
      CameraHeight: this.newSite.cameraHeight,
      FieldOfView: this.newSite.fieldOfView,
      ImageWidth: this.newSite.imageWidth,
      ImageHeight: this.newSite.imageHeight,
      CustomerId: this.newSite.customerId,
      Latitude: this.newSite.latitude,
      Longitude: this.newSite.longitude,
      DemUrl: this.newSite.demUrl,
      StorageBaseUrl: this.newSite.storageBaseUrl,
      PitchError: this.newSite.pitchError,
      RollError: this.newSite.rollError,
      YawError: this.newSite.yawError,
      NextScheduledPatrol: this.newSite.nextScheduledPatrol
    }).subscribe(() => {
      this.toasterService.success('Site added');
      this.loadSites(1);
    });
  }

  // ---- Edit ----

  public openEditModal(row: Site, modalRef: { show: () => void }): void {
    this.editSite = { ...row };
    this.editTouched = {};
    modalRef.show();
  }

  public markEditFieldTouched(field: string): void {
    this.editTouched[field] = true;
  }

  public isEditSiteValid(): boolean {
    return !!(this.editSite.name && this.editSite.siteUri)
      && this.isNonNegative(this.editSite.cameraAltitude)
      && this.editSite.cameraElevation != null
      && this.isNonNegative(this.editSite.cameraHeight)
      && this.editSite.fieldOfView != null
      && this.isNonNegative(this.editSite.imageWidth)
      && this.isNonNegative(this.editSite.imageHeight);
  }

  public saveEditSite(): void {
    ['name', 'siteUri', 'cameraAltitude', 'cameraElevation', 'cameraHeight', 'fieldOfView', 'imageWidth', 'imageHeight']
      .forEach(field => this.editTouched[field] = true);

    if (!this.isEditSiteValid()) {
      this.toasterService.error('Please fill in all required fields');
      return;
    }

    this.fireForesightService.FireForesightUpdateSite({
      id: this.editSite.siteId,
      Name: this.editSite.name,
      SiteUri: this.editSite.siteUri,
      CameraAltitude: this.editSite.cameraAltitude,
      CameraElevation: this.editSite.cameraElevation,
      CameraHeight: this.editSite.cameraHeight,
      FieldOfView: this.editSite.fieldOfView,
      ImageWidth: this.editSite.imageWidth,
      ImageHeight: this.editSite.imageHeight,
      CustomerId: this.editSite.customerId,
      Latitude: this.editSite.latitude,
      Longitude: this.editSite.longitude,
      DemUrl: this.editSite.demUrl,
      StorageBaseUrl: this.editSite.storageBaseUrl,
      PitchError: this.editSite.pitchError,
      RollError: this.editSite.rollError,
      YawError: this.editSite.yawError,
      NextScheduledPatrol: this.editSite.nextScheduledPatrol
    }).subscribe(() => {
      this.toasterService.success('Site updated');
      this.loadSites();
    });
  }
}
